import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { APP_LOCALES, setAppLanguage } from '@/i18n';

/**
 * Settings card with a single dropdown for language selection. Replaces the
 * previous grid - much cleaner, scales as we add more locales, and matches
 * the rest of the Settings UI which uses native-style selects.
 */
export const LanguageSettings = () => {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState(false);

  const currentCode = i18n.resolvedLanguage || i18n.language || 'en';

  const handleChange = async (code: string) => {
    if (code === currentCode || pending) return;
    setPending(true);
    try {
      await setAppLanguage(code);
      const meta = APP_LOCALES.find((l) => l.code === code);
      if (meta) toast.success(meta.nativeName);
    } catch (err) {
      console.error('[LanguageSettings] failed to switch language:', err);
      toast.error(t('settings.languageChangeError'));
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          {t('settings.language', 'Language')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          {t('settings.languageSubtitle')}
        </p>
        <Select value={currentCode} onValueChange={handleChange} disabled={pending}>
          <SelectTrigger className="w-full">
            <SelectValue />
            {pending && <Loader2 className="h-4 w-4 animate-spin text-primary ml-2" />}
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            sideOffset={4}
            avoidCollisions={false}
            className="max-h-[min(60vh,420px)] overflow-y-auto"
          >
            {APP_LOCALES.map((locale) => (
              <SelectItem key={locale.code} value={locale.code}>
                <span className="flex items-baseline gap-2">
                  <span className="font-medium">{locale.nativeName}</span>
                  <span className="text-xs text-muted-foreground">
                    {locale.englishName}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
};
